import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import {
  AppBar,
  Toolbar,
  IconButton,
  Icon,
  Badge,
  Tooltip,
} from "@mui/material";

import MDBox from "components/MDBox";
import Breadcrumbs from "examples/Breadcrumbs";
import routes from "routes";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const location = useLocation();
  const currentPath = location.pathname;

  const formatName = (str) => {
    if (!str) return "";
    const special = { accountingsessions: "Accounting Sessions" };
    const lower = str.toLowerCase();
    if (special[lower]) return special[lower];

    return str
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (firstChar) => firstChar.toUpperCase())
      .trim();
  };

  const getRouteName = (path) => {
    if (path === "/") return "Dashboard";
    const matchedRoute = routes.find((r) => r.route === path);
    if (matchedRoute) return matchedRoute.name;
    const parts = path.split("/").filter(Boolean);
    return formatName(parts[parts.length - 1]);
  };

  const generateBreadcrumbs = (path) => {
    return path.split("/").filter(Boolean).map(formatName);
  };

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY <= 10) || !fixedNavbar,
      );
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs
            icon="home"
            title={getRouteName(currentPath)}
            route={generateBreadcrumbs(currentPath)}
            light={light}
          />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox
              color={light ? "white" : "inherit"}
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>

              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={() => window.location.assign("/Notifications")}
              >
                <Badge color="error" variant="dot" overlap="circular">
                  <Icon>notifications</Icon>
                </Badge>
              </IconButton>

              <IconButton size="small" color="inherit" sx={navbarIconButton}>
                <Icon>account_circle</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
